import React, { useState } from "react";
import { Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/api";

export const CLIENT_TYPE_LABELS = { particulier: "Particulier", professionnel: "Professionnel", association: "Association" };
export const isProClient = (u) => u?.client_type === "professionnel" || u?.client_type === "association";

const TVA_LABELS = { assujetti: "Assujetti TVA", franchise_base: "Franchise en base", non_assujetti: "Non assujetti", exonere: "Exonéré", autre: "Autre" };

const BASE_FIELDS = [
  ["prenom", "Prénom"], ["nom", "Nom"], ["email", "Email", "email"], ["phone", "Téléphone", "tel"],
  ["type_evenement", "Type d'événement"], ["adresse", "Adresse"], ["adresse_complement", "Complément d'adresse"],
  ["code_postal", "Code postal"], ["ville", "Ville"], ["pays", "Pays"],
];
const PRO_FIELDS = [
  ["raison_sociale", "Raison sociale"], ["nom_commercial", "Nom commercial"], ["siren", "SIREN"], ["siret", "SIRET"],
  ["tva_number", "N° TVA intracom"], ["contact_prenom", "Prénom contact"], ["contact_nom", "Nom contact"],
  ["contact_fonction", "Fonction contact"], ["email_facturation", "Email de facturation", "email"],
];
const ALL_KEYS = ["client_type", "tva_status", ...BASE_FIELDS.map((f) => f[0]), ...PRO_FIELDS.map((f) => f[0])];

function KV({ k, v, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-white/45">{k}</p>
      <p className="text-sm mt-1 break-words">{v || <span className="text-white/30 italic">—</span>}</p>
    </div>
  );
}

function Field({ k, label, type, form, setForm }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">{label}</label>
      <input type={type || "text"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="input-luxe mt-1 w-full" data-testid={`input-user-${k}`} />
    </div>
  );
}

function EditForm({ form, setForm }) {
  const pro = isProClient(form);
  return (
    <div className="space-y-5">
      <div className="flex gap-2 flex-wrap" data-testid="ci-type-group">
        {Object.entries(CLIENT_TYPE_LABELS).map(([v, l]) => (
          <button key={v} type="button" onClick={() => setForm({ ...form, client_type: v })} data-testid={`ci-type-${v}`}
            className={`px-3 py-1.5 text-xs border transition-colors ${form.client_type === v ? "bg-[#DCB457] text-black border-[#DCB457]" : "border-[#DCB457]/30 text-white/70 hover:border-[#DCB457]/60"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
        {BASE_FIELDS.map(([k, l, t]) => <Field key={k} k={k} label={l} type={t} form={form} setForm={setForm} />)}
      </div>
      {pro && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 pt-5 border-t border-[#DCB457]/15" data-testid="ci-pro-edit">
          {PRO_FIELDS.map(([k, l, t]) => <Field key={k} k={k} label={l} type={t} form={form} setForm={setForm} />)}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Statut TVA</label>
            <select value={form.tva_status || ""} onChange={(e) => setForm({ ...form, tva_status: e.target.value })} className="input-luxe mt-1 w-full bg-black/60" data-testid="input-user-tva_status">
              <option value="" className="bg-black">—</option>
              {Object.entries(TVA_LABELS).map(([v, l]) => <option key={v} value={v} className="bg-black">{l}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

function ReadView({ u }) {
  const pro = isProClient(u);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <KV k="Type de client" v={CLIENT_TYPE_LABELS[u.client_type] || "Particulier"} testid="ci-type" />
        {BASE_FIELDS.map(([k, l]) => <KV key={k} k={l} v={u[k]} testid={`ci-${k}`} />)}
        <KV k="Créé le" v={u.created_at?.slice(0, 10)} />
      </div>
      {pro && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 pt-5 border-t border-[#DCB457]/15" data-testid="ci-pro-block">
          {PRO_FIELDS.map(([k, l]) => <KV key={k} k={l} v={u[k]} testid={`ci-${k}`} />)}
          <KV k="Statut TVA" v={TVA_LABELS[u.tva_status]} testid="ci-tva_status" />
        </div>
      )}
    </div>
  );
}

export function ClientInfoSection({ u, id, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const start = () => setForm(Object.fromEntries(ALL_KEYS.map((k) => [k, u[k] || ""])));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.client_type) delete payload.client_type;
      if (!payload.email_facturation) delete payload.email_facturation;
      await adminApi.updateUser(id, payload);
      toast.success("Informations client mises à jour.");
      setForm(null);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const btn = "inline-flex items-center gap-1.5 text-xs px-3 py-1.5 transition-colors";
  return (
    <div className="card-luxe p-6 mb-6" data-testid="sec-user">
      <div className="flex items-start justify-between gap-4 mb-4">
        <h2 className="font-serif-luxe text-xl">Informations client</h2>
        {!form ? (
          <button type="button" onClick={start} data-testid="btn-edit-user" className={`${btn} border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10`}>
            <Pencil size={12} strokeWidth={1.5} /> Modifier
          </button>
        ) : (
          <div className="flex gap-2">
            <button type="button" onClick={save} disabled={saving} data-testid="btn-save-user" className={`${btn} bg-[#DCB457] text-black hover:bg-[#DCB457]/90 disabled:opacity-60`}>
              <Save size={12} strokeWidth={1.7} /> {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
            <button type="button" onClick={() => setForm(null)} disabled={saving} data-testid="btn-cancel-user" className={`${btn} border border-white/20 text-white/65 hover:bg-white/5`}>
              <X size={12} strokeWidth={1.5} /> Annuler
            </button>
          </div>
        )}
      </div>
      {form ? <EditForm form={form} setForm={setForm} /> : <ReadView u={u} />}
    </div>
  );
}
